/**
 * Funciones de comunicación con la Wacom STU-540 via WebHID.
 * Todas son funciones puras async que reciben el HIDDevice como primer argumento.
 */

import {
    STU540_LCD_W, STU540_LCD_H,
    STU_REPORT,
    IMG_FORMAT_24BGR, IMG_FORMAT_1BIT_MONO,
} from './wacomConstants';
import logoSoftnik from '../../image/Logo-softnik-blanco.png';

// ── Helpers de dibujo ───────────────────────────────────────────────

/**
 * Dibuja un rectángulo con esquinas redondeadas en un canvas 2D.
 */
export function roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
}

/**
 * Carga una imagen desde una URL y devuelve un HTMLImageElement.
 */
export function loadImage(src) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
    });
}

// ── Comunicación con el dispositivo ─────────────────────────────────

/**
 * Limpia la pantalla LCD de la STU-540.
 */
export async function clearSTUScreen(device) {
    try {
        await device.sendFeatureReport(STU_REPORT.CLEAR_SCREEN, new Uint8Array([0x00]));
        return true;
    } catch (e) {
        return false;
    }
}

/**
 * Activa o desactiva inking mode (trazos visibles en el LCD).
 */
export async function setInkingMode(device, on) {
    try {
        await device.sendFeatureReport(STU_REPORT.INKING_MODE, new Uint8Array([on ? 0x01 : 0x00]));
        return true;
    } catch (e) {
        return false;
    }
}

/**
 * Restringe el área donde el lápiz puede dibujar en el LCD.
 */
export async function setWritingArea(device, x1, y1, x2, y2) {
    try {
        const data = new Uint8Array(8);
        const view = new DataView(data.buffer);
        view.setUint16(0, x1, true);
        view.setUint16(2, y1, true);
        view.setUint16(4, x2, true);
        view.setUint16(6, y2, true);
        await device.sendFeatureReport(STU_REPORT.WRITING_AREA, data);
        return true;
    } catch (e) {
        return false;
    }
}

/**
 * Envía una imagen 800×480 al LCD en formato 24-bit BGR.
 * Protocolo: WriteImageStart → WriteImageData × N → WriteImageEnd
 */
export async function sendImageToSTU(device, imageData) {
    const rgba = imageData.data;
    const total = STU540_LCD_W * STU540_LCD_H;

    // Convertir RGBA → BGR (el orden que espera la STU-540)
    const bgr = new Uint8Array(total * 3);
    for (let i = 0, j = 0; i < rgba.length; i += 4, j += 3) {
        bgr[j]     = rgba[i + 2]; // Blue
        bgr[j + 1] = rgba[i + 1]; // Green
        bgr[j + 2] = rgba[i];     // Red
    }

    // Detectar tamaño del bloque de datos de imagen
    let reportSize = 256;
    try {
        const probe = await device.receiveFeatureReport(STU_REPORT.WRITE_IMG_DATA);
        reportSize = probe.byteLength + 1;
    } catch (e) { /* usar default */ }

    const chunkDataSize = reportSize - 1 - 2;
    const headerSize = 2;

    await device.sendFeatureReport(STU_REPORT.WRITE_IMG_START, new Uint8Array([IMG_FORMAT_24BGR]));

    for (let off = 0; off < bgr.length; off += chunkDataSize) {
        const remaining = Math.min(chunkDataSize, bgr.length - off);
        const chunk = new Uint8Array(reportSize - 1);
        chunk[0] = remaining & 0xFF;
        chunk[1] = (remaining >> 8) & 0xFF;
        chunk.set(bgr.subarray(off, off + remaining), headerSize);
        await device.sendFeatureReport(STU_REPORT.WRITE_IMG_DATA, chunk);
    }

    await device.sendFeatureReport(STU_REPORT.WRITE_IMG_END, new Uint8Array([0x00]));
}

/**
 * Envía una imagen 800×480 en formato monocromo 1-bit al LCD.
 * ~48,000 bytes vs ~1,152,000 bytes en 24-bit BGR.
 */
export async function sendMonoImageToSTU(device, imageData) {
    const rgba = imageData.data;
    const total = STU540_LCD_W * STU540_LCD_H;

    const mono = new Uint8Array(total / 8);
    for (let i = 0; i < total; i++) {
        const lum = (rgba[i * 4] * 299 + rgba[i * 4 + 1] * 587 + rgba[i * 4 + 2] * 114) / 1000;
        if (lum > 128) {
            mono[i >> 3] |= (0x80 >> (i & 7));
        }
    }

    let reportSize = 256;
    try {
        const probe = await device.receiveFeatureReport(STU_REPORT.WRITE_IMG_DATA);
        reportSize = probe.byteLength + 1;
    } catch (_) { /* usar default */ }

    const chunkDataSize = reportSize - 1 - 2;

    await device.sendFeatureReport(STU_REPORT.WRITE_IMG_START, new Uint8Array([IMG_FORMAT_1BIT_MONO]));

    for (let off = 0; off < mono.length; off += chunkDataSize) {
        const remaining = Math.min(chunkDataSize, mono.length - off);
        const chunk = new Uint8Array(reportSize - 1);
        chunk[0] = remaining & 0xFF;
        chunk[1] = (remaining >> 8) & 0xFF;
        chunk.set(mono.subarray(off, off + remaining), 2);
        await device.sendFeatureReport(STU_REPORT.WRITE_IMG_DATA, chunk);
    }

    await device.sendFeatureReport(STU_REPORT.WRITE_IMG_END, new Uint8Array([0x00]));
}

// ── Pantallas especiales ────────────────────────────────────────────

/**
 * Genera una pantalla de "Cargando..." para mostrar mientras se transfiere la UI completa.
 */
export function renderLoadingSplash() {
    const c = document.createElement('canvas');
    c.width = STU540_LCD_W;
    c.height = STU540_LCD_H;
    const ctx = c.getContext('2d');

    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, 800, 480);

    ctx.fillStyle = '#334155';
    ctx.font = 'bold 28px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('Cargando...', 400, 228);

    ctx.strokeStyle = '#cbd5e1';
    ctx.lineWidth = 2;
    roundRect(ctx, 280, 256, 240, 12, 6);
    ctx.stroke();
    ctx.fillStyle = '#10b981';
    roundRect(ctx, 280, 256, 120, 12, 6);
    ctx.fill();

    return ctx.getImageData(0, 0, 800, 480);
}

/**
 * Genera la pantalla de reposo (wallpaper) con el logo de Softnik.
 */
export async function renderWallpaperImage() {
    const c = document.createElement('canvas');
    c.width = STU540_LCD_W;
    c.height = STU540_LCD_H;
    const ctx = c.getContext('2d');

    // Fondo oscuro con gradiente
    const grad = ctx.createLinearGradient(0, 0, 0, 480);
    grad.addColorStop(0, '#0f172a');
    grad.addColorStop(1, '#1e293b');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 800, 480);

    // Línea decorativa verde
    ctx.fillStyle = '#10b981';
    ctx.fillRect(0, 260, 800, 2);

    // Intentar cargar el logo; si falla, usar texto
    try {
        const logo = await loadImage(logoSoftnik);
        const logoW = 280;
        const logoH = logo.height * (logoW / logo.width);
        ctx.drawImage(logo, (800 - logoW) / 2, 180 - logoH / 2, logoW, logoH);
    } catch (_) {
        ctx.fillStyle = '#FFFFFF';
        ctx.font = 'bold 48px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('SOFTNIK', 400, 180);
    }

    // Subtítulo
    ctx.fillStyle = '#64748b';
    ctx.font = '16px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('Sistema de Gestión Integral para Seguridad Privada', 400, 300);

    // Indicador "Tableta lista"
    ctx.fillStyle = '#10b981';
    ctx.beginPath();
    ctx.arc(320, 420, 5, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = '#94a3b8';
    ctx.font = '14px sans-serif';
    ctx.fillText('Lista para firmar', 400, 420);

    return ctx.getImageData(0, 0, 800, 480);
}
